/**
 * One answer to the owner's feedback round, drawn as a card in the round's chat (`FeedbackReplyCard`).
 *
 * The VMR outreach service writes one ASSISTANT row per new answer (sharecard/FeedbackRounds.passiveNote). Since the
 * card shipped the row carries `metadata.feedbackReplyCard` with the round, the design and the answer; before that it
 * carried only the round id (`metadata.feedbackRoundNote`) and a raw line —
 * `Feedback from Someone (anonymous link) [heart]: «Bold and readable»` — which {@link readFeedbackReplyCard} still
 * turns into a card, with no design facts (the round sheet reads them).
 *
 * The answerer's name and comment are a stranger's words: plain text everywhere, never markup.
 *
 * Mirrors iOS `FeedbackReplyCardData` and Android `FeedbackReplyCardData`.
 */
import { FEEDBACK_REACTIONS, FEEDBACK_REACTION_GLYPH, type FeedbackReaction } from './feedback-page';

export type FeedbackAnswererKind = 'personal' | 'signed_in' | 'whatsapp' | 'anonymous';

export interface FeedbackReplyCardData {
  roundId: string;
  answererKind: FeedbackAnswererKind;
  /** Absent: "Someone". */
  answererName?: string;
  reaction?: FeedbackReaction;
  comment?: string;
  /** Absent on an older row: no counts line. */
  counts?: { answered: number; asked: number; answers: number };
  assetTitle?: string;
  assetPreviewUrl?: string;
  /** The asset as a playable file (a video's mp4), when the round has one. */
  assetMediaUrl?: string;
  designUrl?: string;
  /** Read off the older raw line, not the marker. */
  legacy?: boolean;
}

const KINDS: readonly FeedbackAnswererKind[] = ['personal', 'signed_in', 'whatsapp', 'anonymous'];
const MAX_COMMENT = 500;
/** The names the service wrote when it had none: not names. */
const PLACEHOLDER_NAMES = new Set(['someone (anonymous link)', 'a signed-in reader', 'someone on whatsapp', 'someone']);
const LEGACY_LINE = /^Feedback from (.+?)(?: \[([a-z_]+)\])?(?:: «([\s\S]*)»)?$/;

const text = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);
const https = (value: unknown): string | undefined => {
  const url = text(value);
  return url && /^https:\/\//i.test(url) ? url : undefined;
};

export function isFeedbackReactionToken(value: unknown): value is FeedbackReaction {
  return typeof value === 'string' && (FEEDBACK_REACTIONS as readonly string[]).includes(value);
}

function cleanName(raw: string | undefined): string | undefined {
  if (!raw || PLACEHOLDER_NAMES.has(raw.trim().toLowerCase())) return undefined;
  const name = raw
    .replace(/[\u0000-\u001f«»()[\]]/g, ' ')
    .replace(/\s+via WhatsApp$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60)
    .trim();
  return name && !PLACEHOLDER_NAMES.has(name.toLowerCase()) ? name : undefined;
}

/**
 * The card a row carries, or null when it is not a feedback-round answer. Fail-closed on the round id. `content` lets
 * an older row (no marker) still render as a card.
 */
export function readFeedbackReplyCard(metadata: unknown, content?: string | null): FeedbackReplyCardData | null {
  const meta = (metadata ?? {}) as { feedbackReplyCard?: Record<string, unknown>; feedbackRoundNote?: unknown };
  const card = meta.feedbackReplyCard;
  if (card && typeof card === 'object') {
    const roundId = text(card.roundId);
    if (!roundId) return null;
    const kind = KINDS.includes(card.answererKind as FeedbackAnswererKind) ? (card.answererKind as FeedbackAnswererKind) : 'anonymous';
    const comment = text(card.comment)?.slice(0, MAX_COMMENT);
    return {
      roundId,
      answererKind: kind,
      ...(cleanName(text(card.answererName)) ? { answererName: cleanName(text(card.answererName)) } : {}),
      ...(isFeedbackReactionToken(card.reaction) ? { reaction: card.reaction } : {}),
      ...(comment ? { comment } : {}),
      counts: { answered: count(card.answered), asked: count(card.asked), answers: count(card.answers) },
      ...(text(card.assetTitle) ? { assetTitle: text(card.assetTitle) } : {}),
      ...(https(card.assetPreviewUrl) ? { assetPreviewUrl: https(card.assetPreviewUrl) } : {}),
      ...(https(card.assetMediaUrl) ? { assetMediaUrl: https(card.assetMediaUrl) } : {}),
      ...(https(card.designUrl) ? { designUrl: https(card.designUrl) } : {}),
    };
  }
  const roundId = text(meta.feedbackRoundNote);
  const line = typeof content === 'string' ? LEGACY_LINE.exec(content.trim()) : null;
  if (!roundId || !line) return null;
  const rawName = line[1];
  const comment = text(line[3])?.slice(0, MAX_COMMENT);
  return {
    roundId,
    answererKind: /via WhatsApp$|on WhatsApp$/i.test(rawName) ? 'whatsapp' : 'anonymous',
    ...(cleanName(rawName) ? { answererName: cleanName(rawName) } : {}),
    ...(isFeedbackReactionToken(line[2]) ? { reaction: line[2] } : {}),
    ...(comment ? { comment } : {}),
    legacy: true,
  };
}

/** Which counts line to show, with its i18n key and values; null when the card has none. */
export function feedbackCountsLine(
  card: Pick<FeedbackReplyCardData, 'counts'>,
): { key: 'feedbackReply.progress' | 'feedbackReply.answersOne' | 'feedbackReply.answersOther'; values: Record<string, number> } | null {
  const c = card.counts;
  if (!c) return null;
  if (c.asked > 0) return { key: 'feedbackReply.progress', values: { answered: Math.min(c.answered, c.asked), asked: c.asked } };
  const answers = Math.max(c.answers, c.answered);
  if (answers <= 0) return null;
  return answers === 1 ? { key: 'feedbackReply.answersOne', values: {} } : { key: 'feedbackReply.answersOther', values: { count: answers } };
}

export function feedbackReactionGlyph(reaction: FeedbackReaction): string {
  return FEEDBACK_REACTION_GLYPH[reaction];
}

/** Where "Open design" goes: Wixel's own link to the design, else the playable file (a video), else its still. */
export function feedbackDesignUrl(
  card: Pick<FeedbackReplyCardData, 'designUrl' | 'assetPreviewUrl' | 'assetMediaUrl'>,
): string | undefined {
  return card.designUrl ?? card.assetMediaUrl ?? card.assetPreviewUrl;
}

/** symphony-bff GetFeedbackRound, as `/api/feedback-rounds/[id]` hands it to the browser. */
export interface FeedbackRoundView {
  roundId: string;
  status: 'OPEN' | 'SUMMARIZED' | 'EXPIRED' | 'UNKNOWN';
  openLink: boolean;
  assetTitle?: string;
  assetPreviewUrl?: string;
  assetMediaUrl?: string;
  conversationId?: string;
  asked: number;
  answered: number;
  replies: { from?: string; text?: string; reaction?: FeedbackReaction; at?: string }[];
  summary?: string;
}

const enumName = (value: unknown, prefix: string): string => {
  const raw = typeof value === 'string' ? value : '';
  return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
};

/** The BFF response, narrowed to what the sheet shows. Fail-closed on the round id. */
export function toFeedbackRoundView(response: unknown): FeedbackRoundView | null {
  const r = (response ?? {}) as Record<string, any>;
  const roundId = text(r.roundId);
  if (!roundId) return null;
  const status = enumName(r.status, 'STATUS_');
  const recipients: any[] = Array.isArray(r.recipients) ? r.recipients : [];
  const asked = recipients.filter((p) => enumName(p?.state, 'STATE_') !== 'NOT_SENT').length;
  const answered = recipients.filter((p) => enumName(p?.state, 'STATE_') === 'REPLIED').length;
  const replies = (Array.isArray(r.replies) ? r.replies : []).map((reply: any) => {
    const at = reply?.at instanceof Date ? reply.at.toISOString() : text(reply?.at);
    return {
      ...(cleanName(text(reply?.from)) ? { from: cleanName(text(reply?.from)) } : {}),
      ...(text(reply?.text) ? { text: text(reply?.text)!.slice(0, MAX_COMMENT) } : {}),
      ...(isFeedbackReactionToken(reply?.reaction) ? { reaction: reply.reaction as FeedbackReaction } : {}),
      ...(at ? { at } : {}),
    };
  });
  return {
    roundId,
    status: (['OPEN', 'SUMMARIZED', 'EXPIRED'] as const).find((s) => s === status) ?? 'UNKNOWN',
    openLink: enumName(r.mode, 'MODE_') === 'ANONYMOUS',
    ...(text(r.asset?.title) ? { assetTitle: text(r.asset?.title) } : {}),
    ...(https(r.asset?.previewUrl) ? { assetPreviewUrl: https(r.asset?.previewUrl) } : {}),
    ...(https(r.asset?.mediaUrl) ? { assetMediaUrl: https(r.asset?.mediaUrl) } : {}),
    ...(text(r.conversationId) ? { conversationId: text(r.conversationId) } : {}),
    asked,
    answered,
    replies,
    ...(text(r.summary) ? { summary: text(r.summary) } : {}),
  };
}
